import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Check, KeyRound, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CredentialsPanel
 * Shows a new distributor account's credentials ONCE — username, temp
 * password, and a pre-written email template the agency user can copy and
 * paste into their own email client.
 *
 * Props:
 *   user: { email, first_name, name, agency_name }
 *   tempPassword: string
 *   onDone: () => void   // close
 */
export default function CredentialsPanel({ user, tempPassword, onDone, onManageAccess }) {
  const [copied, setCopied] = useState(null);

  const greeting = user.first_name || (user.name || "there").split(" ")[0];
  const loginUrl = `${BACKEND_URL}/login`;
  const emailTemplate = `Subject: Your ${user.agency_name || "My Rep Agency"} search portal login

Hi ${greeting},

You're approved! Your login for the ${user.agency_name || "My Rep Agency"} product search portal:

Username: ${user.email}
Temporary password: ${tempPassword}

Sign in here: ${loginUrl}

You'll be asked to set a new password the first time you log in.

Need anything? Just reply to this email.

Thanks,
Your ${user.agency_name || "My Rep Agency"} team`;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      // clipboard blocked — silent fallback
    }
  };

  return (
    <div
      className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 space-y-4"
      data-testid="credentials-panel"
    >
      <div className="flex items-start gap-2">
        <KeyRound className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
        <div>
          <div className="font-display font-bold text-emerald-900 tracking-tight">
            Distributor account ready
          </div>
          <p className="text-xs text-emerald-800/80 mt-0.5">
            Copy these credentials and email them to{" "}
            <strong>{user.email}</strong>. This is the ONLY time the temporary
            password will be shown.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <CredField
          label="Username"
          value={user.email}
          copied={copied === "user"}
          onCopy={() => copy(user.email, "user")}
          testId="creds-username"
        />
        <CredField
          label="Temporary password"
          value={tempPassword}
          mono
          copied={copied === "pw"}
          onCopy={() => copy(tempPassword, "pw")}
          testId="creds-password"
        />
      </div>

      <div className="bg-white border border-border rounded-sm">
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data flex items-center gap-1.5">
            <Mail className="w-3 h-3" />
            Email template — copy & send from your own email
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => copy(emailTemplate, "email")}
            className="h-7 px-2 rounded-sm text-xs"
            data-testid="creds-copy-email"
          >
            {copied === "email" ? (
              <>
                <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                Copy email
              </>
            )}
          </Button>
        </div>
        <pre className="text-[11px] text-slate-700 font-mono-data whitespace-pre-wrap p-3 max-h-56 overflow-y-auto">
          {emailTemplate}
        </pre>
      </div>

      <div className="flex justify-end gap-2">
        {onManageAccess && (
          <Button
            variant="outline"
            onClick={onManageAccess}
            className="rounded-sm"
            data-testid="creds-manage-access-btn"
          >
            Set manufacturer access →
          </Button>
        )}
        <Button
          onClick={onDone}
          className="rounded-sm bg-slate-900 hover:bg-slate-800"
          data-testid="creds-done-btn"
        >
          Done
        </Button>
      </div>
    </div>
  );
}

function CredField({ label, value, mono, copied, onCopy, testId }) {
  return (
    <div className="bg-white border border-border rounded-sm">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data px-3 pt-2">
        {label}
      </div>
      <div className="flex items-center justify-between gap-2 px-3 pb-2">
        <span
          className={`text-sm text-slate-900 truncate ${mono ? "font-mono-data" : ""}`}
          data-testid={testId}
        >
          {value}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={onCopy}
          className="h-7 w-7 p-0 rounded-sm shrink-0"
          title="Copy"
        >
          {copied ? (
            <Check className="w-3.5 h-3.5 text-emerald-600" />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
        </Button>
      </div>
    </div>
  );
}
